import * as React from 'react';
import Box from '@mui/material/Box';
import { useUnifiedAuth } from 'components/Common/UnifiedAuthContext';
import { useTheme } from '@mui/material/styles';
import ConsentForm from 'components/Layouts/ConsentForm';
import Spinner from 'components/Common/Spinner';
import Footer from 'components/Layouts/Footer';
import Header from 'components/Layouts/Header';
import { SideNavCtx } from 'components/Layouts/constants';
import { useMediaQuery } from '@mui/material';
import { useHttp } from 'hooks/use-fetch';
import { UserRole } from 'types/NavTreeModel';
import { useQuery } from 'react-query';
import { getFreeTrialOnboardingStatus } from 'pages/Dashboard/services/freeTrial.service';
import OnboardingDialog from 'pages/Dashboard/components/OnboardingDialog';
import { auth } from 'utils/spaUrls';

export const PADDING_RIGHT = '2.8%';

export const PADDING_LEFT = { xs: 1.5, sm: 2.75, xl: 16 };

export default function Borders({ children }: { children: React.ReactNode }) {
  const {
    custom: {
      appBarHeight,
      sidebarWidth,
      backgroundColor,
      demoBarHeight,
      navBarHeight,
    },
  } = useTheme();

  const { isDemoUser, userRoles, http } = useHttp();

  const isChiefAdmin = !!userRoles[UserRole.CHIEF_ADMIN];

  const [scrollPosition, setScrollPosition] = React.useState<number>(0);
  const [isScrolledToBottom, setIsScrolledToBottom] = React.useState<boolean>(false);
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useUnifiedAuth();
  const drawerWidth = sidebarWidth;
  const sideNavCtxValue = React.useMemo(
    () => ({ drawerWidth, scrollPosition, isScrolledToBottom }),
    [drawerWidth, scrollPosition, isScrolledToBottom],
  );

  const isSmallScreen = useMediaQuery('(max-width:1500px)');

  const boxRef = React.useRef<HTMLDivElement>(null);
  const footerRef = React.useRef<HTMLDivElement>(null);

  const handleScroll = React.useCallback(() => {
    if (boxRef.current && footerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = boxRef.current;
      const hasScrolledToBottom = scrollTop
      > scrollHeight
        - (clientHeight
        + footerRef.current.clientHeight);
      setScrollPosition(scrollTop);
      setIsScrolledToBottom(hasScrolledToBottom);
    }
  }, []);

  const [orgInfoToken, setOrgInfoToken] = React.useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = React.useState(false);

  const {
    data: onboardingStatusData,
    isLoading: isOnboardingStatusLoading,
  } = useQuery(
    'free-trial-onboarding-status',
    async () => {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('No token available');
      return getFreeTrialOnboardingStatus(http.get, token);
    },
    {
      enabled: isAuthenticated,
      retry: 1,
    },
  );

  const showPaymentInfo = onboardingStatusData?.onboardingStatus === 'PASSWORD_SET';
  const isInFreeTrialFlow = window.location.pathname.includes(auth.freetrial);

  const handleSilentLogin = React.useCallback((accessToken?: string) => {
    setTimeout(() => {
      if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
      }
      setShowOnboarding(false);
    }, 0);
  }, []);

  React.useEffect(() => {
    if (showPaymentInfo) setShowOnboarding(true);
  }, [showPaymentInfo]);

  React.useEffect(() => {
    if (showOnboarding) {
      getAccessTokenSilently().then((token) => {
        if (typeof token === 'string') {
          setOrgInfoToken(token);
        }
      });
    }
  }, [showOnboarding, getAccessTokenSilently]);

  if (isLoading || isOnboardingStatusLoading) {
    return <Spinner />;
  }
  else if (!isAuthenticated || (isInFreeTrialFlow && isAuthenticated)) {
    return <Box>{children}</Box>;
  }

  if (isAuthenticated && showOnboarding && orgInfoToken) {
    return (
      <OnboardingDialog
        orgInfoToken={orgInfoToken}
        handleSilentLogin={handleSilentLogin}
      />
    );
  }

  const topBarHeight = `(${appBarHeight}  + ${!isChiefAdmin ? navBarHeight : '0px'} + ${isDemoUser ? demoBarHeight : '0px'})`;
  return (
    <SideNavCtx.Provider value={sideNavCtxValue}>
      <Header />
      <Box mt={`calc(${topBarHeight})`} height={`calc(100vh - ${`${topBarHeight}`})`}>
        <ConsentForm>
          <Box
            component='main'
            pt={3}
            bgcolor={backgroundColor}
            height='100%'
            overflow='auto'
            ref={boxRef}
            onScroll={handleScroll}
          >
            <Box
              maxWidth='1500px'
              mx='auto'
              px={isSmallScreen ? 1 : 2}
            >
              {children}
              <Footer footerRef={footerRef} />
            </Box>
          </Box>
        </ConsentForm>
      </Box>
    </SideNavCtx.Provider>
  );
}
